import { api, el } from "./dom";
import { icon } from "./icons";
import { foldSummary, startsOpen } from "../../server/rules/folds";

// Cards that fold (brief 1E-25, DECISIONS 10/30; every record from brief 1E-32, DECISIONS 11/01
// "Records that fit on one screen", mockup v90): every card on a record folds to its heading and
// a one-line summary and opens on a click (its heading or its chevron). Each person's choice is
// kept for them (GET and PUT /api/me/folds), on any device. Fold all and Open all change every
// card on the record; a link or tile that names a card opens it and scrolls to it.

let folds: Record<string, boolean> = {};
let loaded: Promise<void> | null = null;

/** The person's choices, read once per page load. */
export function loadFolds(): Promise<void> {
  loaded ??= api<{ folds: Record<string, boolean> }>("GET", "/api/me/folds").then((r) => {
    if (r.ok) folds = r.body.folds;
  });
  return loaded;
}

/** The person's Fold all or Open all on a kind of record ("client._all"). */
const allKey = (prefix: string) => `${prefix}._all`;

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

/** Every record screen folds its cards with foldCards (a test checks the list). */
export const RECORD_SCREENS = [
  "app/screens/patients/record.ts",
  "app/screens/clients/record.ts",
  "app/screens/people/record.ts",
  "app/screens/billing/billing.ts",
  "app/screens/billing/payments.ts",
  "app/screens/inbox/item.ts",
  "app/screens/inbox/document.ts",
] as const;

export interface FoldOptions {
  /**
   * Cards that start open until the person chooses (by the start of their heading, e.g. "Ready
   * to work"). With a list, every other card starts folded; without one, every card starts open.
   */
  openFirst?: readonly string[];
}

/** One line for a folded card: its own summary, or how many rows and its first chips, or its first line. */
function summaryOf(card: HTMLElement): string {
  const own = card.dataset.summary;
  if (own) return own;
  const head = [...card.children].find((c) => c.classList.contains("cardhead"));
  const hint = head?.querySelector(".small.muted, .muted, .hint")?.textContent ?? "";
  const body = [...card.children].filter((c) => !c.classList.contains("cardhead"));
  const chips: string[] = [];
  for (const b of body)
    for (const c of b.querySelectorAll(".chip")) chips.push(c.textContent?.trim() ?? "");
  // Chips in the heading say the card's state too (Ready, Not ready).
  for (const c of head?.querySelectorAll(".chip") ?? []) chips.unshift(c.textContent?.trim() ?? "");
  return foldSummary({
    rows: card.querySelectorAll("tbody tr").length,
    chips,
    firstLine: body
      .map((b) => b.textContent ?? "")
      .join(" ")
      .replace(/\s+/g, " ")
      .trim(),
    hint,
  });
}

interface Folding {
  key: string;
  label: string;
  set: (folded: boolean) => void;
}
const byCard = new WeakMap<HTMLElement, Folding>();

/** The card's name: its aria-label, else its heading. */
const nameOf = (card: HTMLElement, head: HTMLElement | undefined) =>
  (card.getAttribute("aria-label") ?? head?.querySelector("h2, h3")?.textContent ?? "").trim();

function decorate(card: HTMLElement, prefix: string, opts: FoldOptions) {
  // A card redrawn in place (its heading drawn again) is decorated again.
  if (
    card.dataset.fold &&
    card.querySelector(":scope > .cardhead > .foldbtn, :scope > .cardhead .foldbtn")
  )
    return;
  const head = [...card.children].find((c) => c.classList.contains("cardhead")) as
    HTMLElement | undefined;
  const label = nameOf(card, head);
  if (!head || !label) return;
  const key = `${prefix}.${slug(label)}`;
  card.dataset.fold = key;
  head.classList.add("foldhead");
  const sum = el("span", { class: "foldsum small muted" });
  const btn = el("button", { type: "button", class: "foldbtn ib" }, icon("chevron-down"));
  const set = (folded: boolean) => {
    card.classList.toggle("folded", folded);
    btn.setAttribute("aria-expanded", String(!folded));
    btn.setAttribute("aria-label", `${folded ? "Open" : "Fold"} ${label}`);
    btn.dataset.tip = folded ? "Open" : "Fold";
    sum.textContent = folded ? summaryOf(card) : "";
  };
  byCard.set(card, { key, label, set });
  const toggle = () => {
    const folded = !card.classList.contains("folded");
    set(folded);
    folds[key] = folded;
    void api("PUT", "/api/me/folds", { key, folded });
  };
  btn.addEventListener("click", toggle);
  // A click on the heading opens or folds it too, but not one on a button, link or field there.
  head.addEventListener("click", (e) => {
    const t = e.target as Element;
    if (t.closest("button, a, input, select, textarea, label, summary")) return;
    toggle();
  });
  // At the heading, and the one line under it.
  const h2 = head.querySelector("h2, h3");
  if (h2) {
    // Beside the heading, not in it: the heading's name stays its own.
    h2.before(btn);
    h2.after(sum);
  } else {
    head.prepend(btn);
    head.append(sum);
  }
  // The person's own choice for this card, else their Fold all or Open all on this kind of
  // record, else how the record starts.
  const chosen = folds[key] ?? folds[allKey(prefix)];
  set(chosen ?? !startsOpen(label, opts.openFirst));
}

const cardsIn = (root: HTMLElement) =>
  [...root.querySelectorAll<HTMLElement>("section.card")].filter(
    (c) => !c.closest(".modal, .dlg") && !c.classList.contains("keep"),
  );

/** Folds every card under root, now and as more are drawn, each kept as `prefix.<card>`. */
export function foldCards(root: HTMLElement, prefix: string, opts: FoldOptions = {}): void {
  const run = () => cardsIn(root).forEach((c) => decorate(c, prefix, opts));
  void loadFolds().then(run);
  new MutationObserver(() => void loadFolds().then(run)).observe(root, {
    childList: true,
    subtree: true,
  });
}

/**
 * Fold all (folded) or Open all: every card on the record, kept for this person. It also becomes
 * how this kind of record opens for them (`<prefix>._all`), so cards on a tab not opened yet, or
 * folded or opened one by one before, follow it too.
 */
export async function foldAll(root: HTMLElement, folded: boolean): Promise<void> {
  const keys = new Set<string>();
  const prefixes = new Set<string>();
  for (const c of cardsIn(root)) {
    const f = byCard.get(c);
    if (!f) continue;
    f.set(folded);
    keys.add(f.key);
    prefixes.add(f.key.split(".")[0] ?? "");
  }
  for (const p of prefixes) {
    keys.add(allKey(p));
    for (const k of Object.keys(folds)) if (k.startsWith(`${p}.`)) keys.add(k);
  }
  for (const k of keys) folds[k] = folded;
  if (keys.size) await api("PUT", "/api/me/folds", { keys: [...keys], folded });
}

/** Opens the card whose name starts with `name` (a tile or link that names it) and scrolls to it. */
export function openCard(root: HTMLElement, name: string): boolean {
  const card = cardsIn(root).find((c) => byCard.get(c)?.label.startsWith(name));
  const f = card && byCard.get(card);
  if (!card || !f) return false;
  if (card.classList.contains("folded")) {
    f.set(false);
    folds[f.key] = false;
    void api("PUT", "/api/me/folds", { key: f.key, folded: false });
  }
  card.scrollIntoView({ block: "start", behavior: "smooth" });
  card.querySelector<HTMLElement>(".foldbtn")?.focus({ preventScroll: true });
  return true;
}

/** Fold all and Open all, for a record's header. */
export function foldAllButtons(root: HTMLElement): HTMLElement[] {
  const fold = el("button", { type: "button", class: "btn sm", "data-foldall": "" }, "Fold all");
  const open = el("button", { type: "button", class: "btn sm" }, "Open all");
  fold.addEventListener("click", () => void foldAll(root, true));
  open.addEventListener("click", () => void foldAll(root, false));
  return [fold, open];
}

/**
 * A record screen's cards fold, and Fold all and Open all join the actions in its heading
 * (`.head .actions`, or `.cardhead` of its first card) whenever the record is drawn.
 */
export function foldRecord(root: HTMLElement, prefix: string, opts: FoldOptions = {}): void {
  foldCards(root, prefix, opts);
  const place = () => {
    if (root.querySelector("[data-foldall]")) return;
    const spot = root.querySelector<HTMLElement>(".head .actions");
    if (spot) spot.append(...foldAllButtons(root));
  };
  place();
  new MutationObserver(place).observe(root, { childList: true, subtree: true });
}
